import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Users, 
  Search, 
  UserPlus,
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Calendar,
  Clock,
  Loader2,
  Eye,
  Phone,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  attivo: 'bg-green-100 text-green-800',
  sospeso: 'bg-yellow-100 text-yellow-800',
  cessato: 'bg-red-100 text-red-800',
  bozza: 'bg-slate-100 text-slate-800',
  da_completare: 'bg-orange-100 text-orange-800',
};

export default function VolontariOccasionali() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [volontari, setVolontari] = useState([]);
  const [search, setSearch] = useState('');
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [stats, setStats] = useState({ totale: 0, attivi: 0, sospesi: 0, cessati: 0 });

  useEffect(() => {
    loadVolontari();
  }, []);

  const loadVolontari = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/volontari?tipologia=occasionale`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const occasionali = data.volontari ? data.volontari.filter(v => v.tipologia === 'occasionale' || v.tipologia_volontario === 'occasionale') : [];
        setVolontari(occasionali);
        
        // Calculate stats
        const attivi = occasionali.filter(v => v.stato === 'attivo').length;
        const sospesi = occasionali.filter(v => v.stato === 'sospeso').length;
        const cessati = occasionali.filter(v => v.stato === 'cessato').length;
        setStats({
          totale: occasionali.length,
          attivi,
          sospesi,
          cessati
        });
      } else {
        // Se la risposta non è ok ma non è un errore critico, mostra lista vuota
        setVolontari([]);
        setStats({ totale: 0, attivi: 0, sospesi: 0, cessati: 0 });
      }
    } catch (error) {
      console.error('Errore:', error);
      // Non mostrare errore, semplicemente lista vuota
      setVolontari([]);
      setStats({ totale: 0, attivi: 0, sospesi: 0, cessati: 0 });
    } finally {
      setLoading(false);
    }
  };

  const filteredVolontari = volontari.filter(v => {
    const matchSearch = 
      v.nome?.toLowerCase().includes(search.toLowerCase()) ||
      v.cognome?.toLowerCase().includes(search.toLowerCase()) ||
      v.codice_fiscale?.toLowerCase().includes(search.toLowerCase()) ||
      v.email?.toLowerCase().includes(search.toLowerCase());
    
    const matchStato = filtroStato === 'tutti' || v.stato === filtroStato;
    
    return matchSearch && matchStato;
  });

  return (
    <div className="space-y-6" data-testid="volontari-occasionali-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Volontari Occasionali
          </h1>
          <p className="text-slate-500 mt-1">
            Volontari che partecipano saltuariamente alle attività dell'associazione
          </p>
        </div>
        <Button
          onClick={() => navigate('/volontari/nuovo?tipologia=occasionale')}
          className="bg-[#0055BB] hover:bg-[#003d88]"
          data-testid="add-volontario-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Nuovo Volontario Occasionale
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.totale}</p>
                <p className="text-sm text-slate-500">Totale</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.attivi}</p>
                <p className="text-sm text-slate-500">Attivi</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-600">{stats.sospesi}</p>
                <p className="text-sm text-slate-500">Sospesi</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.cessati}</p>
                <p className="text-sm text-slate-500">Cessati</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-800">Cosa sono i Volontari Occasionali?</p>
              <p className="text-sm text-blue-700 mt-1">
                Sono persone che partecipano saltuariamente alle attività dell'ente, senza un impegno continuativo.
                Non richiedono l'iscrizione nel registro dei volontari vidimato, ma è comunque importante 
                tenerne traccia per assicurazioni e rendicontazione.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome, cognome, codice fiscale o email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filtroStato} onValueChange={setFiltroStato}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-stato">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti gli stati</SelectItem>
                <SelectItem value="attivo">Attivi</SelectItem>
                <SelectItem value="sospeso">Sospesi</SelectItem>
                <SelectItem value="cessato">Cessati</SelectItem>
                <SelectItem value="bozza">Bozza</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#0055BB]" />
            </div>
          ) : filteredVolontari.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Users className="w-12 h-12 mb-4 text-slate-300" />
              <p>Nessun volontario occasionale trovato</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate('/volontari/nuovo?tipologia=occasionale')}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Aggiungi il primo
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Contatti</TableHead>
                  <TableHead>Data Iscrizione</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVolontari.map((v) => (
                  <TableRow key={v.volontario_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{v.cognome} {v.nome}</p>
                        <p className="text-xs text-slate-500">{v.codice_fiscale}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {v.email && (
                          <div className="flex items-center gap-1 text-sm">
                            <Mail className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-600">{v.email}</span>
                          </div>
                        )}
                        {v.telefono && (
                          <div className="flex items-center gap-1 text-sm">
                            <Phone className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-600">{v.telefono}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar className="w-4 h-4" />
                        {v.data_iscrizione ? new Date(v.data_iscrizione).toLocaleDateString('it-IT') : 'N/D'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[v.stato] || statusColors.bozza}>
                        {v.stato?.replace(/_/g, ' ').toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/volontari/${v.volontario_id}`)}
                        data-testid={`view-${v.volontario_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Dettagli
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
